// ResetPasswordPage - S48.4.
// Vista publica para completar el reset de contrasena. Lee el token del
// query string ?token=... (JWT short-lived emitido por SMTP-v2). Pide
// new_password + confirm_password con validaciones locales y POSTea a
// /api/auth/reset-password. En exito: redirige a /login.
import { useEffect, useState, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import apiClient from '../../api/apiClient';
import XLInput from '../../components/xl/XLInput';
import { ensureKeyframes, injectCSS } from '../../design/motion';

const CSS_ID = 'xl-auth-pages-css';
// Las clases xl-auth-* se inyectan tambien por ForgotPasswordPage (mismo
// CSS_ID = idempotente). Definimos el CSS aqui por independencia: si esta
// vista se carga directa, los estilos existen igual.
const CSS = `
.xl-auth-page {
  min-height: 100vh; display: flex; align-items: center; justify-content: center;
  background: var(--xl-color-surface-alt);
  font-family: var(--xl-font-sans); color: var(--xl-color-text);
  padding: 24px;
}
.xl-auth-card {
  width: 100%; max-width: 420px;
  background: var(--xl-color-surface);
  border: 1px solid var(--xl-color-border);
  border-radius: var(--xl-radius-lg);
  box-shadow: var(--xl-shadow-md);
  padding: 28px 28px 22px;
  display: flex; flex-direction: column; gap: 16px;
}
.xl-auth-title { margin: 0; font-size: 22px; font-weight: 600 }
.xl-auth-subtitle {
  margin: -8px 0 0; font-size: 13px; color: var(--xl-color-text-secondary);
}
.xl-auth-banner { padding: 10px 14px; border-radius: var(--xl-radius-md); font-size: 13px }
.xl-auth-banner--ok  { background: rgba(5,150,105,0.10);  color: var(--xl-color-success); border: 1px solid rgba(5,150,105,0.28) }
.xl-auth-banner--err { background: rgba(220,38,38,0.10);  color: var(--xl-color-danger);  border: 1px solid rgba(220,38,38,0.28) }
.xl-auth-btn {
  font: 500 14px/1 var(--xl-font-sans); padding: 12px 16px;
  border-radius: var(--xl-radius-md); border: 1px solid var(--xl-color-primary);
  background: var(--xl-color-primary); color: var(--xl-color-text-inverse);
  cursor: pointer; transition: background 120ms cubic-bezier(0,0,0.2,1);
}
.xl-auth-btn:hover { background: var(--xl-color-primary-hover) }
.xl-auth-btn:focus-visible { outline: 2px solid var(--xl-color-focus); outline-offset: 2px }
.xl-auth-btn:disabled { opacity: 0.45; cursor: not-allowed }
.xl-auth-link {
  text-align: center; font: 500 13px/1.4 var(--xl-font-sans);
  color: var(--xl-color-text-secondary); text-decoration: none;
  padding: 6px 0;
}
.xl-auth-link:hover { color: var(--xl-color-text); text-decoration: underline }
`;

const MIN_PASS = 6;

export default function ResetPasswordPage() {
  useEffect(() => { ensureKeyframes(); injectCSS(CSS_ID, CSS); }, []);
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = useMemo(() => (params.get('token') || '').trim(), [params]);

  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [msg, setMsg] = useState(null);

  const tokenMissing = !token;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMsg(null);
    if (tokenMissing) {
      setMsg({ kind: 'err', text: 'Enlace invalido: falta el token. Solicita uno nuevo desde "Recuperar contrasena".' });
      return;
    }
    if ((newPassword || '').length < MIN_PASS) {
      setMsg({ kind: 'err', text: 'La contrasena debe tener al menos ' + MIN_PASS + ' caracteres.' });
      return;
    }
    if (newPassword !== confirm) {
      setMsg({ kind: 'err', text: 'Las contrasenas no coinciden.' });
      return;
    }
    setSubmitting(true);
    try {
      await apiClient.post('/auth/reset-password', {
        token: token,
        new_password: newPassword,
      });
      setMsg({ kind: 'ok', text: 'Contrasena actualizada. Redirigiendo a login...' });
      setTimeout(() => navigate('/login', { replace: true }), 1200);
    } catch (err) {
      const m = (err && (err.message || (err.data && err.data.error)))
        || 'No se pudo restablecer la contrasena. El enlace puede haber expirado.';
      setMsg({ kind: 'err', text: m });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="xl-auth-page">
      <form onSubmit={handleSubmit} className="xl-auth-card" noValidate>
        <h1 className="xl-auth-title">Cambiar contrasena</h1>
        <p className="xl-auth-subtitle">
          {tokenMissing
            ? 'Este enlace es invalido. Solicita uno nuevo.'
            : 'Define una nueva contrasena para tu cuenta.'}
        </p>
        {msg && (
          <div
            className={'xl-auth-banner xl-auth-banner--' + (msg.kind === 'ok' ? 'ok' : 'err')}
            role={msg.kind === 'ok' ? 'status' : 'alert'}
          >
            {msg.text}
          </div>
        )}
        <XLInput
          label="Nueva contrasena"
          type="password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          required
          autoFocus
          minLength={MIN_PASS}
          disabled={submitting || tokenMissing}
          hint={'Minimo ' + MIN_PASS + ' caracteres.'}
        />
        <XLInput
          label="Confirmar contrasena"
          type="password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          required
          minLength={MIN_PASS}
          disabled={submitting || tokenMissing}
        />
        <button type="submit" className="xl-auth-btn" disabled={submitting || tokenMissing}>
          {submitting ? 'Actualizando...' : 'Cambiar contrasena'}
        </button>
        <Link to="/login" className="xl-auth-link">Volver al login</Link>
      </form>
    </div>
  );
}
